import React, { useState, useCallback, useMemo, lazy, Suspense } from 'react';
import { db } from '../firebase';
import { collection, addDoc } from 'firebase/firestore';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Snackbar,
  Alert,
  Grid,
  Link,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Dialog,
  DialogContent,
} from '@mui/material';
import { Logo } from '../components/Logo';
import { Check } from 'lucide-react';

const PrivacyDialog = lazy(() => import('../components/PrivacyDialog'));

function WaitlistPage() {
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
  });
  const [consentChecked, setConsentChecked] = useState(false);
  const [privacyDialogOpen, setPrivacyDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    // Validate email format
    const emailRegex = /^[^@]+@[^@]+\.[^@]+$/;
    if (!emailRegex.test(formData.email)) {
      setSnackbar({
        open: true,
        message: 'Please enter a valid email address',
        severity: 'error',
      });
      return;
    }

    // Validate all fields are filled
    if (!formData.email || !formData.firstName || !formData.lastName) {
      setSnackbar({
        open: true,
        message: 'Please fill in all fields',
        severity: 'error',
      });
      return;
    }

    setSubmitting(true);

    try {
      await addDoc(collection(db, 'waitlist'), {
        ...formData,
        consentGiven: true,
        consentTimestamp: new Date(),
        createdAt: new Date(),
      });

      // Show success dialog
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2000);

      // Reset form
      setFormData({
        email: '',
        firstName: '',
        lastName: '',
      });
      setConsentChecked(false);
    } catch (error) {
      console.error('Waitlist error:', error);
      setSnackbar({
        open: true,
        message: 'Error joining waitlist. Please try again.',
        severity: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  }, [formData]);

  const handlePrivacyDialog = useCallback((e) => {
    e.preventDefault();
    setPrivacyDialogOpen(true);
  }, []);

  const handleClosePrivacyDialog = useCallback(() => {
    setPrivacyDialogOpen(false);
  }, []);

  const handleSnackbarClose = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  // Memoize static content
  const sideImages = useMemo(() => (
    <>
      <Box
        component="img"
        src="/groni MU1.png"
        alt="Groni MU1"
        sx={{
          position: 'fixed',
          left: '2%',
          top: '65%',
          transform: 'translateY(-65%)',
          height: '61.74vh',
          maxWidth: '31.5vw',
          objectFit: 'contain',
          opacity: 0.95,
          display: { xs: 'none', md: 'block' },
          pointerEvents: 'none'
        }}
      />
      <Box
        component="img"
        src="/groni MU2.png"
        alt="Groni MU2"
        sx={{
          position: 'fixed',
          right: '5%',
          top: '65%',
          transform: 'translateY(-65%)',
          height: '63vh',
          maxWidth: '27vw',
          objectFit: 'contain',
          opacity: 0.8,
          display: { xs: 'none', md: 'block' },
          pointerEvents: 'none'
        }}
      />
    </>
  ), []);

  return (
    <Container maxWidth="sm" sx={{ position: 'relative' }}>
      {sideImages}
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          py: 4,
          position: 'relative',
          zIndex: 1,
          mt: 8
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          mb: 4
        }}>
          <Logo width={160} />
          <Typography variant="h4" component="h1" gutterBottom align="center">
            Join the Waitlist
          </Typography>
          <Typography variant="body1" gutterBottom align="center" color="text.secondary">
            Be the first to know when we launch!
          </Typography>
        </Box>
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 4 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                name="firstName"
                variant="outlined"
                value={formData.firstName}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                name="lastName"
                variant="outlined"
                value={formData.lastName}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email Address"
                name="email"
                type="email"
                variant="outlined"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </Grid>
          </Grid>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={submitting}
            sx={{ mt: 3 }}
          >
            Join Waitlist
          </Button>
          <Typography 
            variant="body2" 
            align="center" 
            sx={{ 
              mt: 2,
              color: 'text.secondary',
              '& a': {
                color: 'primary.main',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }
            }}
          >
            <Link 
              href="https://www.instagram.com/drinkgroni/" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              @drinkgroni
            </Link>
          </Typography>

          <Typography 
            variant="caption" 
            color="text.secondary" 
            align="center"
            sx={{ 
              mt: 4,
              fontSize: '0.7rem',
              display: 'block',
              textAlign: 'center'
            }}
          >
            By submitting this form, you agree to our{' '}
            <Link
              component="button"
              variant="caption"
              onClick={handlePrivacyDialog}
              sx={{
                fontSize: 'inherit',
                verticalAlign: 'baseline',
                textDecoration: 'underline'
              }}
            >
              Privacy Policy
            </Link>
          </Typography>
        </Box>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert severity={snackbar.severity} onClose={handleSnackbarClose}>
            {snackbar.message}
          </Alert>
        </Snackbar>
        <Suspense fallback={<CircularProgress />}>
          <PrivacyDialog 
            open={privacyDialogOpen} 
            onClose={handleClosePrivacyDialog} 
          />
        </Suspense>

        <Dialog
          open={showSuccess}
          onClose={() => setShowSuccess(false)}
          PaperProps={{
            sx: {
              backgroundColor: 'transparent',
              boxShadow: 'none',
              overflow: 'hidden'
            }
          }}
        >
          <DialogContent sx={{ textAlign: 'center', p: 3 }}>
            <Box
              sx={{
                backgroundColor: 'success.main',
                borderRadius: '50%',
                width: 80,
                height: 80,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto'
              }}
            >
              <Check size={40} color="white" />
            </Box>
            <Typography variant="h6" sx={{ color: 'white', mt: 2 }}>
              Successfully Joined!
            </Typography>
          </DialogContent>
        </Dialog>
      </Box>
    </Container>
  );
}

export default WaitlistPage; 